import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { runtimeConfig } from '../config/runtime-config';
import type { SessionDto } from '../api';

/** Role names as stored in the `Role` table. */
export type RoleName = SessionDto['role'];

/** The logged-in user. */
export type Session = {
  userId: number;
  username: string;
  roleId: number;
  role: RoleName;
};

/** Start page of each role. */
export const ROLE_HOME: Record<RoleName, string> = {
  admin: '/home/admin',
  betreuer: '/home/supervisor',
  arbeitsamt: '/employment-office',
  bank: '/bank',
};


const toSession = (dto: SessionDto): Session => ({
  userId: dto.user_id,
  username: dto.username,
  roleId: dto.role_id,
  role: dto.role,
});

/**
 * Login state of the current user as signals.
 *
 * The session token lives in an httpOnly cookie set by the API - JavaScript
 * never sees it, so an XSS attack cannot steal it. After a reload the session
 * is restored with `GET /auth/me` (called once by the route guards).
 * The API enforces all roles itself; the role here only decides what is shown.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly url = runtimeConfig.apiUrl + 'auth/';

  private readonly sessionState = signal<Session | null>(null);
  private restoring: Promise<Session | null> | null = null;

  /** The logged-in user or null. */
  readonly session = this.sessionState.asReadonly();
  readonly isLoggedIn = computed(() => this.sessionState() !== null);
  readonly role = computed(() => this.sessionState()?.role ?? null);
  readonly userId = computed(() => this.sessionState()?.userId ?? null);
  readonly isAdmin = computed(() => this.role() === 'admin');
  /** Start page of the current role (`/login` without session). */
  readonly homeUrl = computed(() => {
    const role = this.role();
    return role ? ROLE_HOME[role] : '/login';
  });

  constructor() {
    // tokens of older versions were stored in localStorage - remove them
    for (const key of ['authToken', 'username', 'role_id', 'user_id']) {
      localStorage.removeItem(key);
    }
  }

  /**
   * Logs in; the API sets the session cookie. Rejects (HTTP 401) for invalid
   * credentials.
   */
  async login(username: string, password: string): Promise<Session> {
    const dto = await firstValueFrom(this.http.post<SessionDto>(`${this.url}login`, { username, password }));
    const session = toSession(dto);
    this.sessionState.set(session);
    return session;
  }

  /** Ends the session on the server and locally. */
  async logout(): Promise<void> {
    this.forget();
    await firstValueFrom(this.http.post(`${this.url}logout`, {})).catch(() => undefined);
  }

  /** Forgets the session locally (e.g. after a 401 from the API). */
  forget(): void {
    this.restoring = null;
    this.sessionState.set(null);
  }

  /**
   * Returns the current session, asking the API once after a reload.
   * Concurrent callers share the request; a missing or expired session
   * resolves to null.
   */
  ensureSession(): Promise<Session | null> {
    const current = this.sessionState();
    if (current) {
      return Promise.resolve(current);
    }
    this.restoring ??= firstValueFrom(this.http.get<SessionDto>(`${this.url}me`))
      .then((dto) => {
        const session = toSession(dto);
        this.sessionState.set(session);
        return session;
      })
      .catch((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return null;
        }
        throw error;
      })
      .finally(() => {
        this.restoring = null;
      });
    return this.restoring;
  }

  /**
   * Asks the API whether this login is still valid (an admin may have ended
   * it). Network problems count as "still logged in" - an offline station
   * must not log itself out.
   */
  async stillLoggedIn(): Promise<boolean> {
    try {
      await firstValueFrom(this.http.get<SessionDto>(`${this.url}me`));
      return true;
    } catch (error) {
      return !(error instanceof HttpErrorResponse && error.status === 401);
    }
  }
}
